import { useState } from 'react';

import { CollapseIcon } from '@ui/components/CollapseIcon';
import { FancyBadge } from '@ui/components/FancyBadge';
import {
  FancyModal,
  FancyModalPanel,
  FancyPanelAction,
  FancyPanelTitle,
} from '@ui/components/panels/FancyPanel';
import { Plural } from '@ui/components/Plural';
import { CameraIcon } from '@ui/icons/CameraIcon';

import styles from './VideoRoomsModal.module.css';

export type VideoRoom = {
  number: number;
  participants: {
    userId: string;
    name: string;
  }[];
};

export type Props = {
  rooms: VideoRoom[];
  // не пришли: участники без комнаты, то есть не получавшие билет
  absent: VideoRoom['participants'];
  // открыть страницу судьи для комнаты
  onOpen: (roomNumber: number) => void;
  onClose: () => void;
};

export function VideoRoomsModal({ rooms, absent, onOpen, onClose }: Props) {
  return (
    <FancyModal
      className={styles.panel}
      contentClassName={styles.content}
      actions={<FancyPanelAction onClick={onClose}>Закрыть</FancyPanelAction>}
      onClose={onClose}
    >
      <Rooms rooms={rooms} absent={absent} onOpen={onOpen} />
    </FancyModal>
  );
}

// Сама панель без оверлея: для сторибука
export function VideoRoomsPanel({ rooms, absent, onOpen, onClose }: Props) {
  return (
    <FancyModalPanel
      className={styles.panel}
      contentClassName={styles.content}
      actions={<FancyPanelAction onClick={onClose}>Закрыть</FancyPanelAction>}
      onClose={onClose}
    >
      <Rooms rooms={rooms} absent={absent} onOpen={onOpen} />
    </FancyModalPanel>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Ключ раздела «Отсутствуют» в наборе раскрытых: комнаты нумеруются с единицы
const ABSENT_KEY = 0;

function Rooms({ rooms, absent, onOpen }: Pick<Props, 'rooms' | 'absent' | 'onOpen'>) {
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set());
  const toggle = (n: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(n) ? next.delete(n) : next.add(n);
      return next;
    });

  return (
    <>
      <FancyPanelTitle>Видеокомнаты</FancyPanelTitle>

      {!rooms.length && <p className={styles.empty}>Комнаты еще не созданы</p>}

      <ul className={styles.rooms}>
        {rooms.map((room) => {
          const isOpen = expanded.has(room.number);
          return (
            <li key={room.number} className={styles.room}>
              <div className={styles.room__row}>
                <button
                  type="button"
                  className={styles.room__toggle}
                  onClick={() => toggle(room.number)}
                >
                  <span className={styles.room__icon}>
                    <CollapseIcon toggled={isOpen} />
                  </span>
                  <span className={styles.room__title}>Комната {room.number}</span>
                  <span className={styles.room__meta}>
                    {room.participants.length}{' '}
                    <Plural
                      cnt={room.participants.length}
                      one="участник"
                      few="участника"
                      many="участников"
                    />
                  </span>
                </button>
                <FancyBadge onClick={() => onOpen(room.number)}>
                  <span className={styles.room__open}>
                    <CameraIcon />
                    Открыть
                  </span>
                </FancyBadge>
              </div>

              {isOpen && (
                <ul className={styles.room__participants}>
                  {room.participants.map((p) => (
                    <li key={p.userId}>{p.name}</li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}

        {/* отсутствующие после комнат: без кнопки, открывать нечего; все пришли — раздела нет */}
        {absent.length > 0 && (
          <li className={styles.absent}>
            <div className={styles.room__row}>
              <button
                type="button"
                className={styles.room__toggle}
                onClick={() => toggle(ABSENT_KEY)}
              >
                <span className={styles.room__icon}>
                  <CollapseIcon toggled={expanded.has(ABSENT_KEY)} />
                </span>
                <span className={styles.room__title}>Отсутствуют</span>
                <span className={styles.room__meta}>
                  {absent.length}{' '}
                  <Plural cnt={absent.length} one="участник" few="участника" many="участников" />
                </span>
              </button>
            </div>

            {expanded.has(ABSENT_KEY) && (
              <ul className={styles.room__participants}>
                {absent.map((p) => (
                  <li key={p.userId}>{p.name}</li>
                ))}
              </ul>
            )}
          </li>
        )}
      </ul>
    </>
  );
}
